import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { MetaTags } from "@/components/seo/MetaTags";
import { useIncidents } from "@/hooks/useIncidents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Search, ArrowRight, Download, Lock, Check, XCircle } from "lucide-react";
import { useProfile } from "@/hooks/useProfile";
import { getIncidentLimit } from "@/lib/plans-config";
import { IncidentDetailDrawer } from "@/components/incidents/IncidentDetailDrawer";

// ────────────────────────────────────────────────────────────
// FORMAT DURATION HELPER
// ────────────────────────────────────────────────────────────
function formatDurationSeconds(seconds: number | null): string {
  if (!seconds || seconds <= 0) return "Ongoing";
  const days = Math.floor(seconds / (24 * 3600));
  const hours = Math.floor((seconds % (24 * 3600)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 && parts.length < 3) parts.push(`${secs}s`);

  return parts.join(" ") || "0s";
}

export default function Incidents() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: allIncidents = [], isLoading } = useIncidents(undefined, 100);
  const { data: profile } = useProfile();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedService, setSelectedService] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedSeverity, setSelectedSeverity] = useState("all");

  const [limit, setLimit] = useState(15);
  const [activeDrawerIncident, setActiveDrawerIncident] = useState<any>(null);

  const filterTime = searchParams.get("time") || "30";

  // Filter criteria
  const filtered = allIncidents.filter((inc: any) => {
    // 1. Time range checks
    const daysAgo = parseInt(filterTime);
    const dateLimit = new Date();
    dateLimit.setDate(dateLimit.getDate() - daysAgo);
    const incidentDate = new Date(inc.started_at);
    if (incidentDate < dateLimit) return false;

    // 2. Search Query
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchId = inc.id.toLowerCase().includes(q);
      const matchMsg = inc.error_message?.toLowerCase().includes(q);
      const matchName = inc.websites?.name?.toLowerCase().includes(q);
      if (!matchId && !matchMsg && !matchName) return false;
    }
    // 3. Service
    if (selectedService !== "all") {
      if (inc.websites?.name !== selectedService) return false;
    }
    // 4. Status
    if (selectedStatus !== "all") {
      if (inc.status !== selectedStatus) return false;
    }
    // 5. Severity
    if (selectedSeverity !== "all") {
      const severity = inc.severity || (inc.status === "ongoing" ? "P0" : "P2");
      if (severity !== selectedSeverity) return false;
    }
    return true;
  });

  const displayed = filtered.slice(0, limit);

  // Extract unique service names for the dropdown filter list
  const services = Array.from(
    new Set(allIncidents.map((inc: any) => inc.websites?.name).filter(Boolean))
  );

  const handleExportCSV = () => {
    const headers = ["ID", "Service", "Error", "Severity", "Started At", "TTR (seconds)", "Status", "Auto-Fixed"];
    const rows = filtered.map((inc: any) => {
      const severity = inc.severity || (inc.status === "ongoing" ? "P0" : "P2");
      const isAuto = inc.error_message?.toLowerCase().includes("rollback") || inc.resolved_by?.toLowerCase().includes("auto");
      return [
        inc.id,
        inc.websites?.name || "API Target",
        inc.error_message || "",
        severity,
        inc.started_at || "",
        inc.duration_seconds || "",
        inc.status,
        isAuto ? "TRUE" : "FALSE"
      ];
    });

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `uptimewing_incidents_export_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto font-mono">
      <MetaTags
        title="Incident logs — UptimeWing"
        description="Review historical uptime, outages, TTR metrics, and deployments correlation details."
        canonical="https://uptimewing.com/incidents"
      />

      <div className="flex items-center justify-between border-b-2 border-black pb-4 select-none">
        <div className="flex items-center gap-2">
          <Link
            to="/dashboard"
            className="flex items-center justify-center h-8 w-8 rounded-none border-2 border-black bg-background hover:bg-accent transition-all"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <h1 className="text-xl font-black uppercase tracking-tight leading-none">Incidents</h1>
        </div>

        {/* Time range selector */}
        <select
          value={filterTime}
          onChange={(e) => setSearchParams({ time: e.target.value })}
          className="h-8 border-2 border-black bg-background px-3 font-mono text-[10px] uppercase font-black tracking-tight"
        >
          <option value="7">Last 7d</option>
          <option value="30">Last 30d</option>
          <option value="90">Last 90d</option>
        </select>
      </div>

      {/* Filter and Search controls */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <div className="relative">
          <Input
            placeholder="Search incident logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-10 rounded-none border-2 border-black bg-background pl-8 focus-visible:ring-0 shadow-none font-bold text-xs"
          />
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
        </div>

        <select
          value={selectedService}
          onChange={(e) => setSelectedService(e.target.value)}
          className="h-10 rounded-none border-2 border-black bg-background px-3 text-xs font-black uppercase"
        >
          <option value="all">All Services</option>
          {services.map((name: any) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>

        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="h-10 rounded-none border-2 border-black bg-background px-3 text-xs font-black uppercase"
        >
          <option value="all">All States</option>
          <option value="ongoing">Investigating</option>
          <option value="resolved">Resolved</option>
        </select>

        <select
          value={selectedSeverity}
          onChange={(e) => setSelectedSeverity(e.target.value)}
          className="h-10 rounded-none border-2 border-black bg-background px-3 text-xs font-black uppercase"
        >
          <option value="all">All Severities</option>
          <option value="P0">P0 - Critical</option>
          <option value="P1">P1 - High</option>
          <option value="P2">P2 - Moderate</option>
          <option value="P3">P3 - Low</option>
        </select>
      </div>

      {/* Main logs list */}
      <div className="border-4 border-black bg-background shadow-[8px_8px_0_0_#000] rounded-none">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b-2 border-black bg-zinc-50 font-black uppercase tracking-tight text-zinc-500 text-[10px]">
                <th className="p-4">ID</th>
                <th className="p-4">Service</th>
                <th className="p-4">Title</th>
                <th className="p-4">Severity</th>
                <th className="p-4">Started</th>
                <th className="p-4">TTR</th>
                <th className="p-4">Auto-Fixed</th>
                <th className="p-4 text-right">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y-2 divide-black">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center uppercase tracking-widest animate-pulse font-bold">
                    Analyzing telemetry history...
                  </td>
                </tr>
              ) : displayed.map((inc: any, index) => {
                const limit = getIncidentLimit(profile);
                const isLocked = index >= limit;
                if (isLocked) {
                  return (
                    <tr
                      key={inc.id}
                      onClick={() => navigate("/settings?tab=plan")}
                      className="bg-zinc-50 opacity-60 cursor-pointer hover:opacity-90"
                    >
                      <td className="p-4"><Lock className="h-3.5 w-3.5" /></td>
                      <td colSpan={6} className="p-4 font-black uppercase text-[10px] tracking-wide text-zinc-500">
                        Upgrade to Analyze Incident #{index + 1}
                      </td>
                      <td className="p-4 text-right">
                        <span className="text-[9px] underline font-black">UPGRADE</span>
                      </td>
                    </tr>
                  );
                }

                const severity = inc.severity || (inc.status === "ongoing" ? "P0" : "P2");
                const isAuto = inc.error_message?.toLowerCase().includes("rollback") || inc.resolved_by?.toLowerCase().includes("auto");

                return (
                  <tr
                    key={inc.id}
                    onClick={() => setActiveDrawerIncident(inc)}
                    className="hover:bg-zinc-50 cursor-pointer transition-colors"
                  >
                    <td className="p-4 font-bold text-zinc-400">#{inc.id.slice(0, 4)}</td>
                    <td className="p-4 font-black uppercase">{inc.websites?.name || "API Target"}</td>
                    <td className="p-4 text-zinc-650 max-w-[150px] truncate">{inc.error_message || "HTTP Fail"}</td>
                    <td className="p-4">
                      <span className={`px-1.5 py-0.5 border text-[9px] font-black uppercase ${
                        severity === "P0" ? "bg-red-50 border-red-300 text-red-700" : "bg-zinc-50 border-zinc-300 text-zinc-600"
                      }`}>
                        {severity}
                      </span>
                    </td>
                    <td className="p-4 text-[10px] text-zinc-500">{inc.started_at ? new Date(inc.started_at).toLocaleDateString() : ""}</td>
                    <td className="p-4 font-bold">{formatDurationSeconds(inc.duration_seconds)}</td>
                    <td className="p-4">
                      {isAuto ? (
                        <span className="text-[10px] font-black text-green-600 uppercase">Yes</span>
                      ) : (
                        <span className="text-[10px] font-bold text-zinc-400 uppercase">No</span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <span className={`font-black uppercase px-2 py-0.5 text-[9px] border-2 border-black ${
                        inc.status === "resolved" ? "bg-status-up/25 text-status-up" : "bg-status-down/25 text-status-down"
                      }`}>
                        {inc.status === "resolved" ? "Resolved" : "Investigating"}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {!isLoading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-12 text-center text-zinc-400 font-bold uppercase">
                    No incidents in last {filterTime}d. Great job!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom export actions */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 font-mono text-xs">
        <Button
          onClick={handleExportCSV}
          className="rounded-none border-2 border-black bg-white text-black hover:bg-black hover:text-white font-black uppercase tracking-wider text-xs gap-2"
        >
          <Download className="h-4 w-4" /> Export CSV
        </Button>
        <span className="font-bold text-zinc-400 uppercase">
          Showing {displayed.length} of {filtered.length} incidents
        </span>
        {filtered.length > limit && (
          <button
            onClick={() => setLimit((prev) => prev + 15)}
            className="hover:underline font-black uppercase tracking-widest text-primary"
          >
            [Load More]
          </button>
        )}
      </div>

      {/* Sheet Drawer overlay details */}
      <IncidentDetailDrawer
        open={!!activeDrawerIncident}
        onOpenChange={(o) => !o && setActiveDrawerIncident(null)}
        incident={activeDrawerIncident}
      />
    </div>
  );
}
